/**
 * theme/theme.js -- Port COMPLETO de src/js/theme.js (app/frontend),
 * incluida la parte de "tema de login" que el spike anterior había
 * dejado fuera a proposito. Ahora SI hace falta: la pantalla de login
 * real necesita `applyLoginTheme()` + `LOGIN_BRAND_LABELS` igual que el
 * original. Cero cambios de logica -- mismo STORAGE_KEY
 * ('solicitudesbi_tema', via window.__THEME_KEY__) para que
 * `public/theme-boot.js` (copiado verbatim) siga siendo compatible sin
 * tocarle una linea.
 */
import { api } from '../lib/api.js';

const STORAGE_KEY = window.__THEME_KEY__ || 'solicitudesbi_tema';
const VALID_THEMES = ['w2', 'b3', 'rs', 'mr', 'or', 'w2d', 'b3d', 'rsd', 'mrd', 'ord'];

export function normalizeTheme(value) {
  const code = String(value || '').trim().toLowerCase();
  return VALID_THEMES.includes(code) ? code : null;
}

export function splitTheme(value) {
  const code = normalizeTheme(value) || 'w2';
  const isDark = code.length > 2;
  return {
    code,
    brand: isDark ? code.slice(0, 2) : code,
    scheme: isDark ? 'dark' : 'light',
  };
}

export function buildTheme(brand, scheme) {
  return scheme === 'dark' ? `${brand}d` : brand;
}

export function themeFromUrl() {
  return normalizeTheme(new URLSearchParams(window.location.search).get('t'));
}

export function getStoredTheme() {
  try {
    return normalizeTheme(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function applyTheme(theme) {
  const normalized = normalizeTheme(theme);
  if (!normalized) return null;
  const { brand, scheme } = splitTheme(normalized);
  document.documentElement.setAttribute('data-theme', brand);
  document.documentElement.setAttribute('data-color-scheme', scheme);
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
  } catch {
    /* modo privado: el atributo ya quedo aplicado, solo no persiste */
  }
  // dashboard.js (Chart.js) escucha este evento para repintarse: sus
  // colores salen de variables CSS via getComputedStyle, que no cambian
  // solas nada mas por cambiar el atributo data-theme -- alguien tiene
  // que avisarles que ya cambiaron.
  window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme: normalized, brand, scheme } }));
  return normalized;
}

export async function applyThemeFromBackend() {
  const preferred = themeFromUrl() || getStoredTheme();
  if (preferred) return applyTheme(preferred);

  const res = await api.getTheme();
  if (res.ok) return applyTheme(res.tema);
  return null;
}

const LOGIN_STORAGE_KEY = 'solicitudesbi_tema_login';
const VALID_LOGIN_THEMES = ['w2', 'b3'];

export const LOGIN_BRAND_LABELS = {
  w2: 'Walmart de México',
  b3: 'Bodega Aurrera',
};

export function normalizeLoginTheme(value) {
  const code = String(value || '').trim().toLowerCase();
  return VALID_LOGIN_THEMES.includes(code) ? code : null;
}

export function getStoredLoginTheme() {
  try {
    return normalizeLoginTheme(localStorage.getItem(LOGIN_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeLoginTheme(value) {
  const normalized = normalizeLoginTheme(value);
  if (!normalized) return null;
  try {
    localStorage.setItem(LOGIN_STORAGE_KEY, normalized);
  } catch {
    /* modo privado: se pierde al cerrar, no es critico */
  }
  return normalized;
}

export function loginThemeFromUrl() {
  const fromUrl = themeFromUrl();
  if (!fromUrl) return null;
  return normalizeLoginTheme(splitTheme(fromUrl).brand);
}

export async function applyLoginTheme(value) {
  let brand = normalizeLoginTheme(value) || getStoredLoginTheme();

  if (!brand) {
    brand = loginThemeFromUrl();
    if (brand) storeLoginTheme(brand);
  }

  if (!brand) {
    try {
      const res = await api.getTheme();
      if (res.ok) brand = normalizeLoginTheme(splitTheme(res.tema).brand);
    } catch {
      /* sin red: cae al default de abajo */
    }
  }

  brand = brand || 'w2';
  document.documentElement.setAttribute('data-theme', brand);
  document.documentElement.setAttribute('data-color-scheme', 'light');
  return brand;
}

export const BRAND_LABELS = { w2: 'Walmart', b3: 'Bodega', rs: 'Rosa', mr: 'Morado', or: 'Oro' };
